import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronDown, Check, Search, Loader2, Eraser } from "lucide-react";
import api, { erroMsg } from "./api";
import {
  brl, dataBR, paBR, trunc, cnpjFmt, btn, inputCls, CampoData, Toolbar, Paginacao, useBusca, Cabecalho,
  AcoesDocumento, BotaoGerar, useGerar, Buscando, Alerta,
} from "./ui";

const POR_PAGINA = 50;
const ROTA_BASE = "/app/dashboard-federal";
const VAZIO = { empresas: [], tipo: "Todas", data_ini: "", data_fim: "", codigo_receita: "", numero_documento: "", valor_min: "", valor_max: "" };

function Campo({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-xs font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}

function MultiEmpresas({ empresas, selecionadas, setSelecionadas }) {
  const [aberto, setAberto] = useState(false);
  const [txt, setTxt] = useState("");
  const ref = useRef();
  useEffect(() => {
    const f = (e) => ref.current && !ref.current.contains(e.target) && setAberto(false);
    document.addEventListener("mousedown", f);
    return () => document.removeEventListener("mousedown", f);
  }, []);
  const todas = selecionadas.includes("TODAS");
  const toggle = (cnpj) => {
    if (cnpj === "TODAS") return setSelecionadas(todas ? [] : ["TODAS"]);
    const base = selecionadas.filter((c) => c !== "TODAS");
    setSelecionadas(base.includes(cnpj) ? base.filter((c) => c !== cnpj) : [...base, cnpj]);
  };
  const t = txt.toLowerCase().replace(/[./-]/g, "");
  const lista = empresas.filter((e) => `${e.razao_social} ${e.cnpj}`.toLowerCase().includes(t));
  const nome = (c) => {
    if (c === "TODAS") return "Todas as empresas";
    const e = empresas.find((x) => x.cnpj === c);
    return e ? trunc(e.razao_social, 28) : c;
  };

  return (
    <div className="relative" ref={ref}>
      <div onClick={() => setAberto(true)}
        className="flex min-h-[36px] cursor-text flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-2 py-1 pr-14 focus-within:ring-2 focus-within:ring-primary/30">
        {selecionadas.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-2.5 pr-1 text-xs font-medium text-primary">
            {nome(c)}
            <button onClick={(e) => { e.stopPropagation(); toggle(c); }} className="rounded-full p-0.5 hover:bg-primary/20"><X className="h-3 w-3" /></button>
          </span>
        ))}
        <input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder={selecionadas.length ? "" : "Todas as empresas"}
          className="min-w-[120px] flex-1 bg-transparent px-1 text-sm text-foreground outline-none placeholder:text-muted-foreground" />
        <span className="absolute right-2 top-2 flex items-center gap-1 text-muted-foreground">
          {selecionadas.length > 0 && <button onClick={(e) => { e.stopPropagation(); setSelecionadas([]); }} className="hover:text-foreground"><X className="h-4 w-4" /></button>}
          <ChevronDown className="h-4 w-4" />
        </span>
      </div>
      {aberto && (
        <div className="absolute z-40 mt-1.5 max-h-64 w-full overflow-y-auto rounded-xl border border-border bg-card p-1 shadow-xl">
          {[{ cnpj: "TODAS", razao_social: "Todas as empresas" }, ...lista].map((e) => {
            const sel = e.cnpj === "TODAS" ? todas : selecionadas.includes(e.cnpj);
            return (
              <button key={e.cnpj} onClick={() => toggle(e.cnpj)}
                className={`flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted ${sel ? "text-primary" : "text-foreground"}`}>
                <span className="min-w-0">
                  <span className="block truncate font-medium">{e.razao_social}</span>
                  {e.cnpj !== "TODAS" && <span className="block text-[11px] text-muted-foreground">{cnpjFmt(e.cnpj)}</span>}
                </span>
                {sel && <Check className="h-4 w-4 shrink-0" />}
              </button>
            );
          })}
          {lista.length === 0 && <p className="px-3 py-3 text-sm text-muted-foreground">Nenhuma empresa encontrada.</p>}
        </div>
      )}
    </div>
  );
}

export default function ComprovantesFiltroPage() {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [f, setF] = useState(VAZIO);
  const [resultado, setResultado] = useState(null);
  const [consultando, setConsultando] = useState(false);
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const [visiveis, setVisiveis] = useState({});
  const [buscando, setBuscando] = useState(null);
  const [alerta, setAlerta] = useState(null);
  const gerar = useGerar(setBuscando, setAlerta);
  const set = (k) => (v) => setF((o) => ({ ...o, [k]: v?.target ? v.target.value : v }));

  useEffect(() => { api.get("/empresas").then((r) => setEmpresas(r.data)).catch(() => {}); }, []);
  useEffect(() => setPagina(1), [busca, resultado]);

  const num = (v) => (v === "" ? undefined : Number(String(v).replace(/\./g, "").replace(",", ".")));
  const buscar = async () => {
    setConsultando(true);
    try {
      const { data } = await api.get("/filtro", {
        params: {
          empresas: f.empresas.length ? f.empresas.join(",") : "TODAS",
          tipo: f.tipo,
          data_ini: f.data_ini || undefined,
          data_fim: f.data_fim || undefined,
          codigo_receita: f.codigo_receita || undefined,
          numero_documento: f.numero_documento || undefined,
          valor_min: num(f.valor_min),
          valor_max: num(f.valor_max),
        },
      });
      setResultado(data);
    } catch (e) {
      setAlerta({ titulo: "Não foi possível filtrar", msg: erroMsg(e) });
    } finally {
      setConsultando(false);
    }
  };

  const atualizarDoc = (novo) => setResultado((rs) => rs.map((d) => (d.id === novo.id ? { ...d, ...novo } : d)));

  const colunas = [
    { key: "razao_social", label: "Empresa", fixa: true },
    { key: "cnpj", label: "CNPJ", exportar: (l) => cnpjFmt(l.cnpj) },
    { key: "tipo", label: "Tipo" },
    { key: "numero_documento", label: "Nº documento" },
    { key: "periodo_apuracao", label: "P. apuração", exportar: (l) => paBR(l.periodo_apuracao) },
    { key: "data_arrecadacao", label: "Pago em", exportar: (l) => dataBR(l.data_arrecadacao) },
    { key: "data_vencimento", label: "Vencimento", exportar: (l) => dataBR(l.data_vencimento) },
    { key: "codigo_receita", label: "Cód. receita" },
    { key: "valor_total", label: "Valor", exportar: (l) => brl(l.valor_total) },
    { key: "acoes", label: "Ações", semExport: true },
    { key: "consultar", label: "Comprovante", semExport: true },
  ];
  const vis = (k) => visiveis[k] !== false;
  const linhas = useBusca(resultado || [], ["razao_social", "cnpj", "numero_documento", "codigo_receita", "tipo"], busca);
  const paginaLinhas = linhas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);
  const totalValor = linhas.reduce((s, d) => s + Number(d.valor_total || 0), 0);
  const nCols = colunas.filter((c) => vis(c.key)).length;

  return (
    <div className="space-y-6">
      <Cabecalho
        titulo="Filtro avançado"
        subtitulo="Encontre comprovantes por empresa, tipo, período, código de receita, número ou valor."
        trilha={[
          { label: "Dashboard Federal", onClick: () => navigate(ROTA_BASE) },
          { label: "Comprovantes de Pagamento", onClick: () => navigate(`${ROTA_BASE}/comprovantes`) },
          { label: "Filtro avançado" },
        ]}
      />

      <section className="rounded-2xl border border-border bg-card p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Campo label="Empresa(s)" className="md:col-span-2">
            <MultiEmpresas empresas={empresas} selecionadas={f.empresas} setSelecionadas={set("empresas")} />
          </Campo>
          <Campo label="Tipo de documento">
            <select value={f.tipo} onChange={set("tipo")} className={inputCls}>
              {["Todas", "DARF", "DAS", "DAE", "DJE"].map((t) => <option key={t} value={t}>{t === "Todas" ? "Todos" : t}</option>)}
            </select>
          </Campo>
          <Campo label="Código da receita">
            <input value={f.codigo_receita} onChange={set("codigo_receita")} placeholder="Ex.: 1099" className={inputCls} />
          </Campo>
          <Campo label="Período de pagamento" className="md:col-span-2">
            <div className="flex items-center gap-2">
              <CampoData valor={f.data_ini} onChange={set("data_ini")} placeholder="De" />
              <span className="text-xs text-muted-foreground">até</span>
              <CampoData valor={f.data_fim} onChange={set("data_fim")} placeholder="Até" />
            </div>
          </Campo>
          <Campo label="Número do documento">
            <input value={f.numero_documento} onChange={set("numero_documento")} placeholder="Nº do DARF/DAS" className={inputCls} />
          </Campo>
          <Campo label="Faixa de valor (R$)">
            <div className="flex items-center gap-2">
              <input value={f.valor_min} onChange={set("valor_min")} placeholder="Mín." inputMode="decimal" className={inputCls} />
              <span className="text-xs text-muted-foreground">a</span>
              <input value={f.valor_max} onChange={set("valor_max")} placeholder="Máx." inputMode="decimal" className={inputCls} />
            </div>
          </Campo>
        </div>
        <div className="mt-5 flex justify-end gap-2 border-t border-border pt-4">
          <button onClick={() => { setF(VAZIO); setResultado(null); }} className={btn.ghost}><Eraser className="h-4 w-4" /> Limpar</button>
          <button onClick={buscar} disabled={consultando} className={btn.primary}>
            {consultando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Buscar comprovantes
          </button>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card">
        {resultado !== null && (
          <div className="flex flex-col gap-3 border-b border-border p-5">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <h2 className="text-base font-semibold text-foreground">{linhas.length} comprovante(s)</h2>
              <span className="text-sm text-muted-foreground">Total pago <b className="text-foreground">{brl(totalValor)}</b></span>
            </div>
            <Toolbar colunas={colunas} visiveis={visiveis} setVisiveis={setVisiveis} linhas={linhas} nome="filtro_comprovantes"
              busca={busca} setBusca={setBusca} />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {colunas.map((c) => vis(c.key) && (
                  <th key={c.key} className={`whitespace-nowrap px-3 py-3 ${c.key === "razao_social" ? "pl-5 text-left" : c.key === "valor_total" ? "text-right" : c.key === "consultar" ? "pr-5 text-right" : "text-left"}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {resultado === null && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">Defina os filtros acima e clique em “Buscar comprovantes”.</td></tr>
              )}
              {resultado !== null && paginaLinhas.length === 0 && (
                <tr><td colSpan={nCols} className="py-14 text-center text-sm text-muted-foreground">Nenhum comprovante encontrado com esses filtros.</td></tr>
              )}
              {paginaLinhas.map((d) => (
                <tr key={d.id} className="transition-colors hover:bg-muted/30">
                  <td className="py-3 pl-5 pr-3 font-medium text-foreground" title={d.razao_social}>{trunc(d.razao_social, 30)}</td>
                  {vis("cnpj") && <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-muted-foreground">{cnpjFmt(d.cnpj)}</td>}
                  {vis("tipo") && <td className="px-3 py-3"><span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">{d.tipo}</span></td>}
                  {vis("numero_documento") && <td className="px-3 py-3 font-mono text-xs text-foreground">{d.numero_documento}</td>}
                  {vis("periodo_apuracao") && <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{paBR(d.periodo_apuracao)}</td>}
                  {vis("data_arrecadacao") && <td className="whitespace-nowrap px-3 py-3 text-foreground">{dataBR(d.data_arrecadacao)}</td>}
                  {vis("data_vencimento") && <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{dataBR(d.data_vencimento)}</td>}
                  {vis("codigo_receita") && <td className="px-3 py-3 text-muted-foreground">{d.codigo_receita}</td>}
                  {vis("valor_total") && <td className="whitespace-nowrap px-3 py-3 text-right font-semibold tabular-nums text-foreground">{brl(d.valor_total)}</td>}
                  {vis("acoes") && <td className="px-3 py-2"><AcoesDocumento doc={d} setAlerta={setAlerta} /></td>}
                  {vis("consultar") && <td className="py-2 pl-3 pr-5 text-right"><BotaoGerar small doc={d} onGerar={() => gerar(d, atualizarDoc)} /></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {resultado !== null && <Paginacao pagina={pagina} setPagina={setPagina} total={linhas.length} porPagina={POR_PAGINA} />}
      </section>

      {buscando && <Buscando msg={buscando} />}
      {alerta && <Alerta {...alerta} onOk={() => setAlerta(null)} />}
    </div>
  );
}
